import { DateTimeFormatter } from '@js-joda/core';

import { extractValidEmails } from '#/shared/utils/formHelper';

import { ApproveTimesheetApiData } from '../types/ApproveTimesheetApi';
import { TimesheetApprovalFormValues } from '../types/TimesheetApprovalFormValues';

type MapTimesheetApprovalFormValuesToApproveTimesheetApiDataParams = {
  formValues: TimesheetApprovalFormValues;
  paidBreakExceptionIsEnabled: boolean;
  signatureAsArrayIsEnabled: boolean;
};

const CHUNK_SIZE = 512;

/**
 * Splits the signature into chunks of 512 characters. This is a workaround due to some hospitals submissions failing when it has over 1024 characters.
 * @param signature - Approver signature base64 string
 * @returns Array of signature chunks of 512 characters
 */
const splitSignatureIntoChunks = (signature: string): string[] => {
  if (signature.length === 0) {
    return [];
  }

  const chunkCount = Math.ceil(signature.length / CHUNK_SIZE);
  const chunks: string[] = new Array(chunkCount);

  for (let i = 0; i < chunkCount; i++) {
    const start = i * CHUNK_SIZE;
    chunks[i] = signature.slice(start, start + CHUNK_SIZE);
  }

  return chunks;
};

/**
 * Maps `TimesheetApprovalFormValues` data to the `ApproveTimesheetApiData`.
 * When `signatureAsArrayIsEnabled` is true, the signature is split into chunks of 512 characters.
 * @param params The parameters object containing form values and feature flag values
 */
export const mapTimesheetApprovalFormValuesToApproveTimesheetApiData = (
  params: MapTimesheetApprovalFormValuesToApproveTimesheetApiDataParams,
): ApproveTimesheetApiData => {
  const { formValues, paidBreakExceptionIsEnabled, signatureAsArrayIsEnabled } = params;
  const emailCopyTo: string[] | undefined = extractValidEmails(formValues.approverEmails);

  const signature = signatureAsArrayIsEnabled ? splitSignatureIntoChunks(formValues.signature) : formValues.signature;

  const data: ApproveTimesheetApiData = {
    authorized_by: {
      name: formValues.name.trim(),
      role: formValues.role.trim(),
      department: formValues.department.trim() || undefined, // || instead of ?? to ensure undefined is passed
      signed_at: formValues.signedAt?.format(DateTimeFormatter.ISO_OFFSET_DATE_TIME) ?? '',
      signature,
    },
    approver_notes: formValues.approverNotes?.trim() || undefined, // || instead of ?? to ensure undefined is passed
    email_copy_to: emailCopyTo,
  };

  if (paidBreakExceptionIsEnabled) {
    return {
      ...data,
      paid_break_decisions: formValues.paidBreakExceptions.map(exception => ({
        shift_date: exception.shift_date,
        approver_decision: exception.approver_decision,
        approver_notes: exception.approver_notes?.trim() || undefined,
      })),
    };
  }

  return data;
};
